import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import type {
  Appointment,
  CreateAppointmentData,
  PaginatedData,
  UpdateAppointmentData,
} from '../types'
import {
  appointmentsService,
  type AppointmentListParams,
} from '../services/api/appointments'

export function useAppointments(params?: AppointmentListParams) {
  const serialized = params ? JSON.stringify(params) : 'default'
  const queryKey = useMemo(
    () => ['appointments', serialized],
    [serialized]
  )

  return useQuery({
    queryKey,
    queryFn: async (): Promise<PaginatedData<Appointment>> => {
      const result = await appointmentsService.list(params)

      if (!result.success || !result.data) {
        throw new Error(
          result.message ?? 'Impossible de récupérer les rendez-vous'
        )
      }

      return result.data
    },
    staleTime: 0,
  })
}

export function useCreateAppointment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateAppointmentData) => {
      const result = await appointmentsService.create(payload)

      if (!result.success || !result.data) {
        throw new Error(result.message ?? 'Création du rendez-vous impossible')
      }

      return result.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      toast.success('Rendez-vous créé avec succès')
    },
    onError: error => {
      const message =
        error instanceof Error ? error.message : "Une erreur s'est produite"
      toast.error(message)
    },
  })
}

export function useUpdateAppointment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: UpdateAppointmentData
    }) => {
      const result = await appointmentsService.update(id, data)

      if (!result.success || !result.data) {
        throw new Error(
          result.message ?? 'Mise à jour du rendez-vous impossible'
        )
      }

      return result.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({
        queryKey: ['appointments', { id: variables.id }],
      })
      toast.success('Rendez-vous mis à jour')
    },
    onError: error => {
      const message =
        error instanceof Error ? error.message : "Une erreur s'est produite"
      toast.error(message)
    },
  })
}

export function useDeleteAppointment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await appointmentsService.delete(id)

      if (!result.success) {
        throw new Error(
          result.message ?? 'Suppression du rendez-vous impossible'
        )
      }

      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      toast.success('Rendez-vous supprimé')
    },
    onError: error => {
      const message =
        error instanceof Error ? error.message : "Une erreur s'est produite"
      toast.error(message)
    },
  })
}
